/**
 * /journal — list of post-call reflection cards.
 *
 * Loads paginated entries from GET /api/journal. Tap an entry to expand
 * inline (no separate page — keeps the calm one-screen feel).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, Star, StarOff, Trash2, ChevronLeft } from "lucide-react";

import AuthNav from "@/components/AuthNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getMe, listJournal, patchJournal, deleteJournal } from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return ""; }
}

export default function Journal() {
  const navigate = useNavigate();
  const [authReady, setAuthReady] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [favOnly, setFavOnly] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { body } = await getMe();
      if (cancelled) return;
      if (!body || body.scope !== "user") {
        navigate("/", { replace: true });
        return;
      }
      setAuthReady(true);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  useEffect(() => {
    if (!authReady) return;
    let cancelled = false;
    setLoading(true);
    listJournal({ q: q.trim() || undefined, favorited: favOnly || undefined })
      .then(({ body }) => {
        if (cancelled) return;
        setEntries((body && body.items) || []);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [authReady, q, favOnly]);

  const empty = !loading && entries.length === 0;

  const toggleStar = async (e) => {
    const next = !e.favorited;
    setEntries((prev) => prev.map((x) => x.id === e.id ? { ...x, favorited: next } : x));
    await patchJournal(e.id, { favorited: next });
  };

  const removeEntry = async (e) => {
    if (!window.confirm("Delete this reflection? You can't undo this.")) return;
    setEntries((prev) => prev.filter((x) => x.id !== e.id));
    setOpenId(null);
    await deleteJournal(e.id);
  };

  if (!authReady) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="journal-page">
      <AuthNav />
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <header className="mb-6">
          <h1
            className="font-serif text-3xl tracking-tight"
            data-testid="journal-heading"
          >
            Your reflections
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Quiet notes after each call. Private. Yours.
          </p>
        </header>

        <div className="mb-6 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="journal-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search titles and summaries…"
              className="h-10 rounded-full pl-9"
            />
          </div>
          <Button
            variant={favOnly ? "default" : "outline"}
            onClick={() => setFavOnly((v) => !v)}
            data-testid="journal-fav-filter"
            className="h-10 rounded-full px-4"
          >
            <Star className="mr-1.5 h-4 w-4" />
            Starred
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : empty ? (
          <div
            data-testid="journal-empty"
            className="rounded-3xl border border-border/60 bg-[hsl(var(--card))] px-6 py-12 text-center"
          >
            <p className="font-serif text-lg italic text-muted-foreground">
              Your reflections will appear here after your first call.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="journal-list">
            {entries.map((e) => {
              const open = openId === e.id;
              return (
                <li
                  key={e.id}
                  data-testid={`journal-entry-${e.id}`}
                  className="overflow-hidden rounded-2xl border border-border/60 bg-[hsl(var(--card))] transition hover:shadow-sm"
                >
                  <button
                    type="button"
                    onClick={() => setOpenId(open ? null : e.id)}
                    className="flex w-full items-start gap-3 px-5 py-4 text-left"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h2 className="font-serif text-lg text-foreground">{e.title}</h2>
                        {e.favorited && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />}
                      </div>
                      <p className="mt-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                        {formatDate(e.created_at)}
                      </p>
                      <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                        {e.summary}
                      </p>
                    </div>
                  </button>

                  {open && (
                    <div className="border-t border-border/40 px-5 py-4 text-sm" data-testid={`journal-detail-${e.id}`}>
                      <Section label="Summary">{e.summary}</Section>
                      <Section label="Suggested prayer">{e.prayer}</Section>
                      <Section label="Scripture"><span className="italic">{e.scripture}</span></Section>
                      <Section label="A gentle next step">{e.next_step}</Section>
                      <div className="mt-4 flex flex-wrap items-center gap-2">
                        <Button size="sm" variant="ghost" onClick={() => toggleStar(e)} data-testid={`journal-star-${e.id}`}>
                          {e.favorited ? <><StarOff className="mr-1.5 h-3.5 w-3.5" />Unstar</> : <><Star className="mr-1.5 h-3.5 w-3.5" />Star</>}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => removeEntry(e)} data-testid={`journal-delete-${e.id}`}>
                          <Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="mt-3">
      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className="mt-1 text-foreground/90">{children}</p>
    </div>
  );
}
